/**
 * Authentication Service
 * Handles B2B and B2C authentication with our monolithic backend
 * Replaces Auth0 authentication
 */

import axios from 'axios';
import tokenManager from './tokenManager';
import {
  getCurrentOrgSlug,
  getCurrentBranchSlug,
  getCurrentParentSlug,
  getTenantScopedStorageKey,
  getStatelessPathname,
  buildStateAwarePath,
} from '../../utils/tenant';
import { getCurrentClientSlug } from '../../utils/subdomain';
import { buildTenantUrl } from '../../utils/urls';

export const resolveStytchBaseUrl = () => {
  if (import.meta.env.VITE_STYTCH_LOGIN_BASE_URL) {
    return import.meta.env.VITE_STYTCH_LOGIN_BASE_URL;
  }

  const { protocol, hostname, port } = window.location;
  const baseDomain = import.meta.env.VITE_APP_BASE_DOMAIN;

  const buildOrigin = (host) => `${protocol}//${host}${port ? ':' + port : ''}`;
  const hostWithoutPort = hostname.split(':')[0];

  // For localhost without subdomain
  if (hostWithoutPort === 'localhost' || hostWithoutPort === '127.0.0.1') {
    return buildOrigin('localhost');
  }

  const isLocalHostname = (candidate) =>
    candidate.endsWith('.localhost') || candidate.endsWith('.local.test');

  // Preserve full subdomain for local development domains
  if (isLocalHostname(hostWithoutPort)) {
    return buildOrigin(hostWithoutPort);
  }

  // If the base domain is configured and matches the current host, preserve it as well
  if (baseDomain && hostWithoutPort.endsWith(`.${baseDomain}`)) {
    return buildOrigin(hostWithoutPort);
  }

  // For all other cases, use the full origin
  return window.location.origin;
};

const _rawApiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001/api/v1';
const API_BASE =
  typeof window !== 'undefined' &&
  window.location?.protocol === 'https:' &&
  !import.meta.env.PROD
    ? `${window.location.origin}/api/v1`
    : _rawApiBase;

class AuthService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE,
      withCredentials: true, // CRITICAL: Include cookies in cross-origin requests
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: import.meta.env.VITE_API_TIMEOUT || 30000,
    });
    
    // Attach tenant host header so the backend middleware can resolve tenant context
    // from the browser's hostname (e.g. branch-testt.demo-platform.miralabs.ai)
    this.apiClient.interceptors.request.use(
      (config) => {
        if (typeof window !== 'undefined') {
          config.headers['X-Tenant-Host'] = window.location.hostname;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * B2C Consumer Login
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Response with token and user data
   */
  async loginConsumer(email, password) {
    try {
      const response = await this.apiClient.post('/auth/b2c/login', {
        email,
        password,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * B2C Consumer Registration
   * @param {Object} userData - Registration data
   * @returns {Promise} - Response with created user
   */
  async registerConsumer(userData) {
    try {
      const response = await this.apiClient.post('/auth/b2c/register', {
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName,
        last_name: userData.lastName,
        phone_number: userData.phoneNumber,
        referral_code: userData.referralCode,
        loan_purpose: userData.loanPurpose,
        property_type: userData.propertyType,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * B2B Magic Link - Send magic link to email
   * @param {string} email - User email address
   * @param {string} organizationId - Stytch organization ID
   * @param {string} loginRedirectUrl - Optional custom login redirect URL
   * @param {string} signupRedirectUrl - Optional custom signup redirect URL
   * @param {Object} context - Optional context for smart routing
   * @returns {Promise} - Response with magic link status
   */
  async sendB2BMagicLink(email, organizationId, loginRedirectUrl, signupRedirectUrl, context = {}) {
    console.log('[AuthService] sendB2BMagicLink called with:');
    console.log('[AuthService] email param:', email);
    console.log('[AuthService] email type:', typeof email);
    console.log('[AuthService] email raw:', JSON.stringify(email));
    console.log('[AuthService] email length:', email?.length);
    console.log('[AuthService] PASSED loginRedirectUrl:', loginRedirectUrl);
    console.log('[AuthService] PASSED signupRedirectUrl:', signupRedirectUrl);
    
    try {
      // Import subdomain utility for context detection
      const { getSubdomain } = await import('../../utils/subdomain');
      
      // Use exact URLs that match Stytch configuration - preserve subdomain context
      const baseUrl = resolveStytchBaseUrl();

      console.log('[AuthService] DEBUG URL generation:');
      console.log('  - window.location.hostname:', window.location.hostname);
      console.log('  - window.location.origin:', window.location.origin);
      console.log('  - Resolved baseUrl:', baseUrl);

      // Determine appropriate callback URL based on context
      const subdomain = getSubdomain();
      const branchSlugFromContext = context.branchSlug || getCurrentBranchSlug();
      const parentSlug = context.parentSlug || getCurrentParentSlug();
      const clientTenantSlug = context.clientTenantSlug || getCurrentClientSlug();
      const orgSlugForState = branchSlugFromContext || context.organizationSlug || subdomain || clientTenantSlug;
      const branchSlug = branchSlugFromContext || null;
      const currentStatelessPath = getStatelessPathname();
      const isPlatformFlow = context.isPlatformFlow || currentStatelessPath.startsWith('/platform');

      let defaultCallbackRoute;
      if (isPlatformFlow && !subdomain) {
        // Platform admin authentication - no organization context
        defaultCallbackRoute = '/platform/auth/callback';
      } else if (subdomain || orgSlugForState) {
        // Client organization authentication - has organization context
        defaultCallbackRoute = '/auth/callback';
      } else {
        // Default to client callback for standard B2B flows
        defaultCallbackRoute = '/auth/callback';
      }
      
      // Build callback URLs with organization context preservation
      const defaultRedirectUrl = buildTenantUrl({
        clientSlug: clientTenantSlug || orgSlugForState,
        clientTenantSlug: clientTenantSlug || orgSlugForState,
        branchSlug,
        parentSlug,
        path: defaultCallbackRoute,
      });

      const appendTenantQuery = (targetUrl) => {
        try {
          return new URL(targetUrl);
        } catch (error) {
          return new URL(targetUrl, window.location.origin);
        }
      };

      const redirectUrl = appendTenantQuery(defaultRedirectUrl);

      const normalizeRedirectTarget = (candidateUrl) => {
        if (!candidateUrl) {
          return redirectUrl.toString();
        }

        try {
          const parsed = new URL(candidateUrl);
          const pathWithQuery = `${parsed.pathname}${parsed.search || ''}`;
          const normalized = buildTenantUrl({
            clientSlug: clientTenantSlug || orgSlugForState,
            clientTenantSlug: clientTenantSlug || orgSlugForState,
            branchSlug,
            parentSlug,
            path: pathWithQuery || defaultCallbackRoute,
          });
          const finalUrl = appendTenantQuery(normalized);
          if (parsed.hash) {
            finalUrl.hash = parsed.hash;
          }
          return finalUrl.toString();
        } catch (err) {
          console.warn('[AuthService] Failed to normalize redirect target, falling back to default', err);
          return redirectUrl.toString();
        }
      };

      console.log('[AuthService] DEBUG final URLs:');
      console.log('  - baseUrl used:', baseUrl);
      console.log('  - defaultCallbackRoute:', defaultCallbackRoute);
      console.log('  - defaultRedirectUrl:', redirectUrl.toString());

      console.log('[AuthService] Sending magic link with context:', {
        email,
        organizationId,
        subdomain,
        organizationSlug: orgSlugForState,
        branchSlug,
        parentSlug,
        clientTenantSlug,
        isPlatformFlow,
        callbackRoute: defaultCallbackRoute,
        redirectUrl: redirectUrl.toString()
      });
      
      // Double-check email value before creating payload
      console.log('[AuthService] Pre-payload email check:');
      console.log('  - email variable:', email);
      console.log('  - email type:', typeof email);
      console.log('  - email raw:', JSON.stringify(email));
      
      console.log('[AuthService] DEBUG URL selection:');
      console.log('  - loginRedirectUrl param:', loginRedirectUrl);
      console.log('  - signupRedirectUrl param:', signupRedirectUrl);
      const finalLoginRedirect = normalizeRedirectTarget(loginRedirectUrl);
      const finalSignupRedirect = normalizeRedirectTarget(signupRedirectUrl);

      console.log('  - defaultRedirectUrl calculated:', redirectUrl.toString());
      console.log('  - Will use login URL:', finalLoginRedirect);
      console.log('  - Will use signup URL:', finalSignupRedirect);

      const requestPayload = {
        email_address: email,
        organization_id: organizationId,
        login_redirect_url: finalLoginRedirect,
        signup_redirect_url: finalSignupRedirect
      };
      
      console.log('[AuthService DEBUG] Sending magic link request payload:', requestPayload);
      console.log('[AuthService DEBUG] Email in payload:', requestPayload.email_address);
      console.log('[AuthService DEBUG] Email raw in payload:', JSON.stringify(requestPayload.email_address));
      console.log('[AuthService DEBUG] Email length:', requestPayload.email_address?.length);
      console.log('[AuthService DEBUG] Full payload JSON:', JSON.stringify(requestPayload));
      
      // Log the actual axios request
      console.log('[AuthService] About to make axios request');
      console.log('[AuthService] Request URL:', `${this.apiClient.defaults.baseURL}/auth/magic-link/send`);
      console.log('[AuthService] Request body:', JSON.stringify(requestPayload));
      
      const response = await this.apiClient.post('/auth/magic-link/send', requestPayload);
      
      console.log('[AuthService] Response received:', response.data);
      
      // Log the magic link URLs for debugging
      if (response.data && typeof response.data === 'object') {
        console.log('[AuthService] Magic link response keys:', Object.keys(response.data));
        if (response.data.magic_link || response.data.login_url || response.data.signup_url) {
          console.log('[AuthService] Generated magic link URLs:', {
            magic_link: response.data.magic_link,
            login_url: response.data.login_url, 
            signup_url: response.data.signup_url
          });
        }
      }
      
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Send magic link for consumer login (single-organization B2C)
   * Uses the dedicated B2C magic-link endpoint.
   * @param {string} email - Consumer email
   * @param {Object} options - Optional overrides
   * @param {string} options.organizationId - Override organization ID
   * @param {string} options.branchId - Override branch ID
   * @param {string} options.organizationSlug - Override organization slug
   * @param {string} options.branchSlug - Override branch slug
   * @param {string} options.loanOfficerId - Optional inviting loan officer ID
   */
  async sendConsumerMagicLink(email, options = {}) {
    const {
      organizationId,
      branchId,
      organizationSlug,
      branchSlug,
      loanOfficerId,
    } = options;

    const payload = {
      email,
      ...(organizationId ? { organization_id: organizationId } : {}),
      ...(branchId ? { branch_id: branchId } : {}),
      ...(organizationSlug ? { organization_slug: organizationSlug } : {}),
      ...(branchSlug ? { branch_slug: branchSlug } : {}),
      ...(loanOfficerId ? { loan_officer_id: loanOfficerId } : {}),
    };

    const response = await this.apiClient.post('/b2c/auth/magic-link/send', payload);
    return response.data;
  }

  /**
   * Authenticate magic link token
   * @param {string} token - Magic link token from URL
   * @returns {Promise} - Response with session and user data
   */
  async authenticateMagicLink(token) {
    try {
      const response = await this.apiClient.get('/auth/magic-link/authenticate', {
        params: { token },
      });
      
      // Store session data if successful
      if (response.data?.success && response.data?.data?.session) {
        const { user, organization } = response.data.data;
        
        // Persist scoped auth state for the current tenant
        const orgSlug = getCurrentOrgSlug();

        if (user) {
          tokenManager.setUserData(user);

          if (user.role) {
            const selectionKey = getTenantScopedStorageKey('userSelection', orgSlug);
            localStorage.setItem(selectionKey, user.role);
            sessionStorage.setItem(selectionKey, user.role);
          }
        }

        // Store organization context with tenant scoping
        if (organization) {
          const organizationKey = getTenantScopedStorageKey('organization', orgSlug);
          localStorage.setItem(organizationKey, JSON.stringify(organization));
        }
      }
      
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Discover organizations for a given email domain
   * @param {string} email - Email address to search for organizations
   * @returns {Promise} - Response with list of organizations
   */
  async discoverOrganizations(email) {
    try {
      console.log('[AuthService] Discovering organizations for email:', email);
      const response = await this.apiClient.post('/auth/discovery/organizations', {
        email: email
      });
      console.log('[AuthService] Discovery response:', response.data);
      return response.data;
    } catch (error) {
      // Return empty organizations list on error to allow graceful fallback
      console.error('Organization discovery failed:', error);
      return { organizations: [] };
    }
  }

  /**
   * Resolve canonical login portal for an email (consumer vs employee).
   * @param {string} email - User email
   * @returns {Promise<Object>} - { user_type, redirect_url, ... }
   */
  async resolveLogin(email) {
    try {
      const response = await this.apiClient.post('/auth/resolve-login', {
        email,
      });
      return response.data;
    } catch (error) {
      // Fail open so login can continue even if resolver is unavailable
      console.error('Resolve login failed:', error);
      return { user_type: 'unknown' };
    }
  }

  /**
   * Check if an email is a member of an organization
   * @param {string} email - Email address to check
   * @param {string} organizationId - Organization ID to check membership for
   * @returns {Promise} - Response with membership status
   */
  async checkMembership(email, organizationId) {
    try {
      console.log('[AuthService] Checking membership for:', { email, organizationId });
      const response = await this.apiClient.post('/auth/check-membership', {
        email,
        organization_id: organizationId
      });
      console.log('[AuthService] Membership check response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Membership check failed:', error);
      // Fail open for better UX - if the API fails, allow the user to proceed
      return { 
        is_member: true, 
        error: true,
        message: 'Unable to verify membership status' 
      };
    }
  }

  /**
   * Lookup organization by subdomain
   * @param {string} subdomain - Organization subdomain to lookup
   * @returns {Promise} - Response with organization details
   */
  async lookupOrganization(subdomain) {
    try {
      // Add development header if in localhost
      const headers = {};
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        headers['X-Organization-Subdomain'] = subdomain;
      }
      
      // Use the v1 endpoint that Alex is implementing
      const response = await this.apiClient.get(`/auth/discovery/organization/${subdomain}`, {
        headers,
      });
      return response.data;
    } catch (error) {
      console.error('Organization lookup failed:', error);
      // Check if it's a 404 (organization not found)
      if (error.response?.status === 404) {
        return { 
          success: false, 
          error: 'Organization not found',
          data: null 
        };
      }
      // For other errors, throw to let the caller handle
      this.handleError(error);
    }
  }

  /**
   * Get current authenticated user by verifying session
   * @param {string} sessionToken - Stytch session token
   * @returns {Promise} - User data
   */
  async getCurrentUser(sessionToken) {
    try {
      const response = await this.apiClient.post('/auth/session/verify', {
        session_token: sessionToken,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get current authenticated user from cookies (no token needed)
   * @returns {Promise} - User data or null if not authenticated
   */
  async getCurrentUserFromCookies() {
    try {
      const response = await this.apiClient.get('/auth/me');
      return response.data;
    } catch (error) {
      // Don't throw on 401 - just means user is not authenticated
      if (error.response?.status === 401) {
        return null;
      }
      this.handleError(error);
    }
  }

  /**
   * Validate token
   * @param {string} token - JWT token to validate
   * @returns {Promise} - Validation response
   */
  async validateToken(token) {
    try {
      const response = await this.apiClient.post('/auth/validate-token', {
        token,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Refresh authentication token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise} - New access token
   */
  async refreshToken(refreshToken) {
    try {
      const payload = refreshToken ? { refresh_token: refreshToken } : {};
      const response = await this.apiClient.post('/auth/session/refresh', payload);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Logout user by revoking session
   * Enhanced with robust error handling and multi-tab sync
   * @param {string} sessionToken - Stytch session token
   * @returns {Promise} - Logout response
   */
  async logout(sessionToken, options = {}) {
    const { skipRedirect = false, redirectTo } = options;

    try {
      await this.apiClient.post('/auth/session/revoke', sessionToken ? { session_token: sessionToken } : {});
      console.log('[AuthService] Session revocation requested');
    } catch (error) {
      console.error('[AuthService] Logout API error:', error);
      throw error;
    } finally {
      if (!skipRedirect) {
        const currentPath = getStatelessPathname();
        let redirectUrl = redirectTo || buildStateAwarePath('/login/b2b');
        if (!redirectTo) {
          if (currentPath.startsWith('/platform')) {
            redirectUrl = '/platform/login';
          } else if (currentPath.includes('/consumer') || currentPath.includes('/b2c')) {
            redirectUrl = buildStateAwarePath('/login/consumer');
          }
        }
        window.location.replace(redirectUrl);
      }
    }

    return { success: true };
  }

  /**
   * Reset password request
   * @param {string} email - User email
   * @returns {Promise} - Reset response
   */
  async resetPassword(email) {
    try {
      const response = await this.apiClient.post('/auth/b2c/reset-password', {
        email,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Verify email with token
   * @param {string} token - Email verification token
   * @returns {Promise} - Verification response
   */
  async verifyEmail(token) {
    try {
      const response = await this.apiClient.post('/auth/b2c/verify-email', {
        token,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Handle B2B OAuth callback - authenticate magic link token
   * Used for Stytch token exchange during B2B authentication flow
   * @param {string} token - Magic link token from URL query parameter
   * @returns {Promise} - Response with authenticated session data
   */
  async handleB2BCallback(token) {
    try {
      // Use the existing authenticateMagicLink method
      const response = await this.authenticateMagicLink(token);
      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Initiate B2B login flow using Stytch magic links
   * Enhanced to use Stytch magic link authentication
   * @param {string} email - User email address (optional)
   * @param {string} organizationId - Stytch organization ID (optional)
   * @returns {Promise|void} - If email provided, sends magic link; otherwise navigates to B2B login
   */
  async initiateB2BLogin(email = null, organizationId = null) {
    if (email && organizationId) {
      // Send magic link directly if email and organization provided
      try {
        // Use exact URLs that match Stytch configuration - preserve subdomain context
        const baseUrl = window.location.origin;
        
        const redirectUrl = `${baseUrl}/auth/callback`;
        
        return await this.sendB2BMagicLink(email, organizationId, redirectUrl, redirectUrl);
      } catch (error) {
        console.error('Failed to send B2B magic link:', error);
        // Fallback to navigation
        window.location.href = '/login/b2b';
      }
    } else {
      // Navigate to the B2B login component which handles magic link flow
      window.location.href = '/login/b2b';
    }
  }

  /**
   * Generate referral link for loan officer
   * @param {Object} data - Referral data
   * @returns {Promise} - Referral link
   */
  async generateReferralLink(data) {
    try {
      // Cookie-based auth - no Authorization header needed
      const response = await this.apiClient.post(
        '/auth/b2c/generate-referral',
        data
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Handle authentication errors
   * @param {Error} error - Error object
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.detail || error.response.data?.message || 'Authentication failed';
      const errorObj = new Error(message);
      errorObj.status = error.response.status;
      errorObj.data = error.response.data;
      throw errorObj;
    } else if (error.request) {
      // Request made but no response
      throw new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      throw error;
    }
  }
}

// Export singleton instance
export default new AuthService();
